import React, { FC, useCallback, useRef } from 'react';

import { Box } from '@mui/system';

import {
  adminActions,
  Agencies,
  convertToDecimals,
  currency,
  getPharmacistBookingsList,
  IAdminPharmacistBookingData,
  ITEMS_PER_PAGE,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { renderScreen } from '../../../../actions/drawerActions';
import {
  ChipType,
  ScreenTypes,
  toWeekdayWeekendFormat,
} from '../../../../helpers/Constants';
import { timeString } from '../../../../helpers/Functions';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useGetItems from '../../../../hooks/useGetItems';
import strings from '../../../../localization';
import CustomAvatarSection from '../../../common/CustomAvatarSection';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { LocationText } from '../../AvailableWorkshifts/AvailableWorkshifts';
import useInfiniteScroll from '../../../../hooks/useInfiniteScroll';
import genericClasses from '../../../../theme/GenericClasses';
import NoAvailabilities from '../../MatchingAvailabilities/NoAvailabilities';

import styles from '../styles';
import useSelfService from '../../../../hooks/useSelfService';
import DrawerListHeader from '../../../common/DrawerListHeader';
import getLongTermContractLabels from '../../../common/LongTermContractLabels';
import CustomChip from '../../../common/CustomChip';

interface ISinglePharmacistBooking {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  data: Array<IAdminPharmacistBookingData>;
  id: string;
}

const successAction = adminActions.getPharmacistBookingsList;

const SinglePharmacistBooking: FC<ISinglePharmacistBooking> = ({
  page,
  setPage,
  data,
  id,
}) => {
  const dispatch = useAppDispatch();
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const isEmpty = data.length !== 0;
  const { getImage } = useGetItems();
  const { isSelfService } = useSelfService();

  const sourceRef = useRef<Agencies>(
    isSelfService ? Agencies.PharmaGap : Agencies.PharmaPlan,
  );

  const observerCallback = useCallback(
    (entries: { isIntersecting: any }[]) => {
      const paginationCondition = data.length >= page * ITEMS_PER_PAGE;

      if (paginationCondition && entries[0].isIntersecting) {
        setPage((prevPage) =>
          prevPage + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, data],
  );

  const { listEndRef } = useInfiniteScroll({ observerCallback });

  const config = useCallback(
    (startDate: string, endDate: string, city: string) => {
      const parsedDate = getDayMonthDateYear(startDate);
      const time = timeString(startDate, endDate, timeFormat);

      return [
        { key: '1', value: `${strings.date}: ${parsedDate}` },
        { key: '2', value: `${strings.time}: ${time}` },
        { key: '3', value: <LocationText label={city} /> },
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const goToPharmacyBookingDetails = (bookingId: string, pharmacistId: string, isAutoSyncRecord: boolean) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 3,
          screenType: ScreenTypes.pharmacistList,
          eventId: bookingId,
          pharmacistId,
          extraParameters: {
            isAutoSyncRecord,
          },
        }),
      );
    };

  const menuActions = (
    bookingId: string,
    pharmacistId: string,
    isAutoSyncRecord: boolean,
  ) =>
    [
      {
        key: '1',
        label: strings.viewDetails,
        onClick: goToPharmacyBookingDetails(
          bookingId,
          pharmacistId,
          isAutoSyncRecord,
        ),
      },
    ];

  const handleFilterUpdate = (source: string) => {
    sourceRef.current = source as Agencies;
    dispatch(
      getPharmacistBookingsList({
        pharmacistId: id,
        paging: { page: 1 },
        source: source as Agencies,
      }),
    );
  };

  return (
    <Box sx={styles.listContainer}>
      <DrawerListHeader
        title={strings.listOfBookedPharmacists}
        showFilter={isSelfService}
        onFilterUpdate={handleFilterUpdate}
      />

      {isEmpty ? (
        <>
          {data?.map((item) => {
            const {
              logoId,
              name,
              startDate,
              endDate,
              city,
              paidHourlyRate,
              bookingId,
              isAutoSyncRecord,
              longTermShiftContractId,
              weekDayPaidHourlyRate,
              weekendPaidHourlyRate,
            } = item ?? {};

            const { RangeLabel } = getLongTermContractLabels({
              successAction,
              contractEndDate: endDate,
              contractStartDate: startDate,
              isLongTerm: !!longTermShiftContractId,
            });

            const userId = item[idStore.id as keyof typeof item] as string;
            const label = longTermShiftContractId
              ? toWeekdayWeekendFormat(
                weekDayPaidHourlyRate,
                weekendPaidHourlyRate,
              )
              : `${currency}${paidHourlyRate}${strings.perHour}`;

            const rangeConfig = [
              { key: '5', value: <RangeLabel sx={styles.zeroMargin} /> },
            ];

            const dataConfig = config(startDate, endDate, city);

            return (
              <Box key={bookingId} sx={styles.items}>
                <CustomAvatarSection
                  avatar={getImage(logoId)}
                  hourlyRate={convertToDecimals(paidHourlyRate) as number}
                  title={name}
                  successAction={successAction}
                  config={[
                    ...rangeConfig,
                    ...(longTermShiftContractId
                      ? dataConfig.slice(2)
                      : dataConfig),
                  ]}
                  menuActions={menuActions(
                    bookingId,
                    userId,
                    !!isAutoSyncRecord,
                  )}
                  showFullImage={false}
                  renderChip={
                    longTermShiftContractId ? (
                      <CustomChip label={label} type={ChipType.hourlyRate} />
                    ) : null
                  }
                />
              </Box>
            );
          })}
          <Box
            component="div"
            ref={listEndRef}
            sx={genericClasses.paginationDiv}
          />
        </>
      ) : (
        <NoAvailabilities message={strings.noDataAvailable} />
      )}
    </Box>
  );
};

export default SinglePharmacistBooking;
